import React, { useState } from 'react';
import { Team, TeamPosition, TeamStrength, CandidateSource } from '../../types/team';
import { PositionCard } from './PositionCard';
import { UserAvatar } from '../Avatar/UserAvatar';
import { ProjectSelector } from './ProjectSelector';

// Person type for drag-drop (minimal fields needed)
interface DroppedPerson {
  id: number;
  Name?: string;
  Title?: string;
  Expertise?: string;
  AdditionalSkills?: string;
}

export interface TeamComposerBarProps {
  team: Team | null;
  positions: TeamPosition[];
  strength: TeamStrength | null;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onAddPosition: (position: Partial<TeamPosition>) => void;
  onNewPosition?: () => void;
  onConfirmPosition: (positionId: number) => void;
  onDeletePosition: (positionId: number) => void;
  onEditPosition?: (positionId: number) => void;
  onAddCandidate: (positionId: number, personId: number, source: CandidateSource) => void;
  onSelectCandidate: (positionId: number, personId: number) => void;
  onRemoveCandidate: (positionId: number, personId: number) => void;
  onReorderCandidates?: (positionId: number, fromIndex: number, toIndex: number) => void;
  onViewCandidate?: (personId: number) => void;
  onCreatePositionFromPerson?: (person: DroppedPerson) => void;
  onSave: () => void;
  onCreate: () => void;
  onRenameTeam?: (name: string) => void;
  // Panel toggle props
  showLeftPanel?: boolean;
  onToggleLeftPanel?: () => void;
  showChat?: boolean;
  onToggleChat?: () => void;
  isAdmin?: boolean;
  onOpenAdmin?: () => void;
  onOpenCandidatePools?: () => void;
  user?: { name: string; email: string; picture?: string } | null;
  onLogout?: () => void;
  onEditTeam?: () => void;
  /** Project for current team */
  project?: { id: number; name: string } | null;
  /** All available projects for the selector dropdown */
  projects?: Array<{ id: number; name: string }>;
  /** Assign/unassign current team to a project */
  onSelectProject?: (projectId: number | null) => void;
  /** Open the create-project dialog */
  onOpenCreateProject?: () => void;
  onEditProject?: (projectId: number) => void;
  // Position filter props
  filterByPositionId?: number | null;
  onFilterByPosition?: (positionId: number) => void;
  // Position highlighting (from chat searching)
  highlightedPositionId?: number | null;
  // Auto-collapse left panel on content interaction
  onContentInteraction?: () => void;
}

const TeamComposerBar: React.FC<TeamComposerBarProps> = ({
  team,
  positions,
  strength,
  isExpanded,
  onExpand,
  onCollapse,
  onAddPosition,
  onNewPosition,
  onConfirmPosition,
  onDeletePosition,
  onEditPosition,
  onAddCandidate,
  onSelectCandidate,
  onRemoveCandidate,
  onReorderCandidates,
  onViewCandidate,
  onCreatePositionFromPerson,
// @ts-ignore
  onSave,
// @ts-ignore
  onCreate,
  onRenameTeam,
  showLeftPanel,
  onToggleLeftPanel,
  showChat,
  onToggleChat,
  isAdmin,
  onOpenAdmin,
  onOpenCandidatePools,
  user,
  onLogout,
  onEditTeam,
  project,
  projects: projectsList,
  onSelectProject,
  onOpenCreateProject,
  onEditProject,
  filterByPositionId,
  onFilterByPosition,
  highlightedPositionId,
  onContentInteraction,
}) => {
  const [isAddPositionDragOver, setIsAddPositionDragOver] = useState(false);

  const confirmedPositions = positions.filter(p => p.status === 'confirmed');
  const proposedPositions = positions.filter(p => p.status === 'proposed');
  const totalCandidates = positions.reduce((sum, p) => sum + (p.candidates?.filter(c => c.status === 'selected').length || 0), 0);
  const totalSlots = positions.reduce((sum, p) => sum + p.count_needed, 0);

  // Handle drag over for Add Position button
  const handleAddPositionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsAddPositionDragOver(true);
  };

  const handleAddPositionDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsAddPositionDragOver(false);
  };

  // Handle drop on Add Position - creates a new position from the person's skills
  const handleAddPositionDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsAddPositionDragOver(false);

    try {
      // Try different MIME types
      let rawData = e.dataTransfer.getData('person');
      if (!rawData) rawData = e.dataTransfer.getData('application/json');
      if (!rawData) rawData = e.dataTransfer.getData('text/plain');
      
      if (!rawData) {
        console.warn('No drag data found');
        return;
      }

      const person: DroppedPerson = JSON.parse(rawData);
      console.log('🎯 Dropped person on Add Position:', person);

      if (onCreatePositionFromPerson) {
        onCreatePositionFromPerson(person);
      } else {
        // Fallback: Create position manually based on person's data
        const skills: string[] = [];
        
        // Extract skills from Expertise
        if (person.Expertise) {
          skills.push(...person.Expertise.split(/[,;]/).map(s => s.trim()).filter(s => s).slice(0, 5));
        }
        
        // Extract skills from AdditionalSkills
        if (person.AdditionalSkills) {
          skills.push(...person.AdditionalSkills.split(/[,;]/).map(s => s.trim()).filter(s => s).slice(0, 3));
        }

        // Use person's title as role name, or generate one
        const role = person.Title || 'New Position';

        onAddPosition({
          role,
          skills_required: [...new Set(skills)].slice(0, 6), // Unique skills, max 6
          priority: 'important',
          count_needed: 1,
        });

        // After creating position, we need to add the person as a candidate
        // This will be handled in the parent component via onCreatePositionFromPerson
      }
    } catch (error) {
      console.error('Failed to parse dropped data on Add Position:', error);
    }
  };

  const [showUserMenu, setShowUserMenu] = useState(false);

  const headerToolbarBtn =
    'flex items-center gap-1.5 shrink-0 px-2 py-1.5 rounded transition-colors text-xs font-medium';

  // Global header with panel toggles (always visible, same height whether expanded or not)
  const GlobalHeader = () => (
    <div className="bg-dark-card border-b border-dark-border px-4 py-2 flex items-center gap-3 min-w-0">
      {/* Left: Logo + tagline */}
      <div className="flex items-center gap-3 mr-auto min-w-0">
        <img src="/branding/logo-full.svg" alt="Talent First" className="h-8 shrink-0" />
        <div className="hidden lg:flex flex-col">
          <span className="text-dark-text font-bold text-sm leading-tight">TalentFirst</span>
          <span className="text-dark-text-secondary text-[10px] font-medium truncate">Find Your Team Member</span>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-dark-bg/50 rounded-xl p-1 shrink-0 flex-wrap justify-end border border-dark-border/50">
        {onOpenCandidatePools && (
          <>
            <button
              type="button"
              onClick={onOpenCandidatePools}
              className={`${headerToolbarBtn} hover:bg-indigo-600/10 text-gray-400 hover:text-indigo-400 px-3`}
              title="Candidate pools"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="hidden sm:inline">Pools</span>
            </button>
            <div className="w-px h-4 bg-gray-700 mx-0.5" />
          </>
        )}
        {isAdmin && onOpenAdmin && (
          <>
            <button
              type="button"
              onClick={onOpenAdmin}
              className={`${headerToolbarBtn} hover:bg-amber-600/10 text-gray-400 hover:text-amber-400 px-3`}
              title="Admin settings"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Admin</span>
            </button>
            <div className="w-px h-4 bg-gray-700 mx-0.5" />
          </>
        )}
        
        {/* Left Panel Toggle */}
        {onToggleLeftPanel && (
          <button
            type="button"
            onClick={onToggleLeftPanel}
            className={`${headerToolbarBtn} ${
              showLeftPanel 
                ? 'bg-indigo-600/20 text-indigo-400' 
                : 'hover:bg-indigo-600/10 text-gray-400 hover:text-indigo-400'
            } px-3`}
            title={showLeftPanel ? 'Hide navigation panel' : 'Show navigation panel'}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
              <line x1="9" y1="3" x2="9" y2="21" strokeWidth={2} />
            </svg>
            <span className="hidden sm:inline">Nav</span>
          </button>
        )}

        {/* Team Composer Toggle */}
        <button
          type="button"
          onClick={isExpanded ? onCollapse : onExpand}
          className={`${headerToolbarBtn} ${
            isExpanded 
              ? 'bg-green-600/20 text-green-400' 
              : 'hover:bg-green-600/10 text-gray-400 hover:text-green-400'
          } px-3`}
          title={isExpanded ? 'Hide team composer' : 'Show team composer'}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
            <line x1="3" y1="9" x2="21" y2="9" strokeWidth={2} />
          </svg>
          <span className="hidden sm:inline">Composer</span>
        </button>

        {/* AI Assistant Toggle */}
        {onToggleChat && (
          <button
            type="button"
            onClick={onToggleChat}
            className={`${headerToolbarBtn} ${
              showChat 
                ? 'bg-brand-blue/20 text-brand-blue' 
                : 'hover:bg-blue-600/10 text-gray-400 hover:text-brand-blue'
            } px-3`}
            title={showChat ? 'Hide AI assistant' : 'Show AI assistant'}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
              <line x1="15" y1="3" x2="15" y2="21" strokeWidth={2} />
            </svg>
            <span className="hidden sm:inline">AI Assistant</span>
          </button>
        )}
      </div>

      {/* User avatar + logout */}
      {user && (
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-800 transition-colors"
            title={user.email}
          >
            <UserAvatar user={user} size="sm" className="w-6 h-6" />
            <span className="text-xs text-gray-400 hidden sm:inline max-w-[100px] truncate">{user.name}</span>
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1">
              <div className="px-3 py-2 border-b border-gray-700">
                <div className="text-xs text-gray-300 font-medium truncate">{user.name}</div>
                <div className="text-[10px] text-gray-500 truncate">{user.email}</div>
              </div>
              {onLogout && (
                <button
                  onClick={() => { setShowUserMenu(false); onLogout(); }}
                  className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-gray-700/50 transition-colors"
                >
                  Sign Out
                </button>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );

  // Collapsed state - just the global header
  if (!isExpanded) {
    return (
      <div data-testid="team-composer-bar">
        <GlobalHeader />
      </div>
    );
  }

  // Expanded state
  return (
    <div data-testid="team-composer-bar">
      {/* Global Header - same as collapsed */}
      <GlobalHeader />
      
      {/* Team Composer Content Panel - full width */}
      <div data-testid="team-composer-expanded" className="bg-dark-card border-b border-dark-border shadow-lg" onClick={onContentInteraction}>
        {/* Team Info Bar */}
        <div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm text-gray-500 flex-shrink-0 flex items-center gap-1">
              Project
              {project && onEditProject && (
                <button
                  type="button"
                  onClick={() => onEditProject(project.id)}
                  className="p-0.5 rounded hover:bg-gray-700 transition-colors text-gray-500 hover:text-yellow-400"
                  title="Edit project"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
              :
            </span>
            {onSelectProject && onOpenCreateProject && projectsList ? (
              <ProjectSelector
                project={project || null}
                projects={projectsList}
                onSelectProject={onSelectProject}
                onOpenCreateProject={onOpenCreateProject}
                onEditProject={onEditProject}
              />
            ) : project ? (
              <button
                type="button"
                onClick={() => onEditProject?.(project.id)}
                className="text-sm font-semibold text-dark-text hover:text-indigo-400 truncate max-w-[280px] transition-colors"
                title="Edit project"
              >
                {project.name}
              </button>
            ) : (
              <span className="text-sm text-gray-500 italic flex-shrink-0">Unassigned</span>
            )}
            <span className="text-gray-600 flex-shrink-0">·</span>
            <span className="text-sm text-gray-500 flex-shrink-0 flex items-center gap-1">
              Team
              {onEditTeam && (
                <button
                  type="button"
                  onClick={onEditTeam}
                  className="p-0.5 rounded hover:bg-gray-700 transition-colors text-gray-500 hover:text-yellow-400"
                  title={team ? 'Edit team' : 'Create team'}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
              :
            </span>
            <input
              type="text"
              defaultValue={team?.name || ''}
              key={team?.id || 'new'} 
              placeholder="Enter team name..."
              onBlur={(e) => {
                const newName = e.target.value.trim();
                if (newName && newName !== (team?.name || '') && onRenameTeam) {
                  onRenameTeam(newName);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none text-sm font-semibold text-dark-text px-1 py-0.5 transition-colors w-full max-w-[600px] placeholder:text-gray-500 placeholder:font-normal"
            />
          </div>

          <div className="flex items-center gap-3">
            {/* Team Strength Indicator */}
            {strength && (
              <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-gray-800">
                <span className="text-xs text-gray-400">Strength:</span>
                <span className={`font-bold ${
                  strength.overall_score >= 80 ? 'text-green-400' :
                  strength.overall_score >= 60 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {strength.overall_score}%
                </span>
              </div>
            )}

            {/* Summary */}
            <div className="text-sm text-gray-400">
              <span className="font-medium text-dark-text">{positions.length}</span> positions •{' '}
              <span className="font-medium text-dark-text">{totalCandidates}</span>/{totalSlots} filled
            </div>

          </div>
        </div>

        {/* Positions Grid */}
      <div className="px-4 py-3 overflow-x-auto">
        <div className="flex gap-3 items-start">
          {/* Confirmed Positions */}
          {confirmedPositions.map(position => (
            <PositionCard
              key={position.id}
              position={position}
              onDelete={onDeletePosition}
              onEdit={onEditPosition}
              onAddCandidate={onAddCandidate}
              onSelectCandidate={onSelectCandidate}
              onRemoveCandidate={onRemoveCandidate}
              onReorderCandidates={onReorderCandidates}
              onViewCandidate={onViewCandidate}
              isFilterActive={filterByPositionId === position.id}
              onFilterByPosition={onFilterByPosition}
              isHighlighted={highlightedPositionId === position.id}
            />
          ))}

          {/* Proposed Positions */}
          {proposedPositions.map(position => (
            <PositionCard
              key={position.id}
              position={position}
              onConfirm={onConfirmPosition}
              onDelete={onDeletePosition}
              onEdit={onEditPosition}
              onAddCandidate={onAddCandidate}
              onSelectCandidate={onSelectCandidate}
              onRemoveCandidate={onRemoveCandidate}
              onReorderCandidates={onReorderCandidates}
              onViewCandidate={onViewCandidate}
              isFilterActive={filterByPositionId === position.id}
              onFilterByPosition={onFilterByPosition}
              isHighlighted={highlightedPositionId === position.id}
            />
          ))}

          {/* Add Position Button — opens PositionEditDialog in create mode */}
          <div
            role="button"
            tabIndex={0}
            data-testid="add-position-btn"
            aria-label="Add Position"
            onClick={() => onNewPosition?.()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNewPosition?.(); }}
            onDragOver={handleAddPositionDragOver}
            onDragLeave={handleAddPositionDragLeave}
            onDrop={handleAddPositionDrop}
            className={`
              min-w-[200px] h-32 rounded-lg border-2 border-dashed transition-all 
              flex flex-col items-center justify-center gap-2 cursor-pointer
              ${isAddPositionDragOver 
                ? 'border-green-400 bg-green-900/30 text-green-400 scale-105 ring-2 ring-green-400/50' 
                : 'border-gray-600 hover:border-brand-blue hover:bg-blue-900/20 text-gray-400 hover:text-blue-400'
              }
            `}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-sm font-medium">
              {isAddPositionDragOver ? 'Drop to create position' : 'Add Position'}
            </span>
            {isAddPositionDragOver && (
              <span className="text-xs opacity-75">from person's skills</span>
            )}
          </div>

          {/* Empty state hint */}
          {positions.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm py-4">
              <span>💡 Ask the AI assistant to suggest positions, or add them manually</span>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default TeamComposerBar;
